import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Megaphone, Plus, Trash2, X, Sparkles, ShoppingCart, Check } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";
import * as api from "./api";
import type { Campaign, DripStep } from "./api";
import CampaignWizard from "./CampaignWizard";
import {
  Pill, StatTile, SectionCard, RadioCards, ChipMultiSelect, MeToolbar, formatPhone,
} from "../me-ui";

// ME → Campaigns (S3/D57), rebuilt in the BatchDialer configuration language (CC-S1/D67):
// config-type bar (New / Create-from-Existing / Recycle) · left settings stepper ·
// center me-ui section cards · live right-side summary · sticky action bar with
// Save-as-Draft. App-only: reuses every existing api function + save path, plus the
// new clone_campaign / recycle_campaign RPCs and the twilio-numbers buy flow. Predictive
// stays gated off (D35). The live dialer / me_call_queue / apply_dial_disposition / runner
// are untouched — Smart Local Presence stores a config flag here only (dialer wiring = CC-S2).

const STATUSES = ["draft", "active", "paused", "completed", "archived"];
const STATUS_TONE: Record<string, "green" | "amber" | "blue" | "grey" | "violet"> = {
  draft: "violet", active: "green", paused: "amber", completed: "blue", archived: "grey",
};
const cap = (s: string) => s.replace(/^\w/, (c) => c.toUpperCase());
const CampaignStatusPill = ({ status }: { status: string }) => (
  <Pill tone={STATUS_TONE[status] ?? "grey"} dot>{cap(status)}</Pill>
);

// Channels (D74). Call is today's dialer base; sms/email are cohort-driven bases; mail is a
// reserved value with no surface yet.
const CHANNELS = ["call", "sms", "email", "mail"] as const;
type Channel = (typeof CHANNELS)[number];
const CHANNEL_LABEL: Record<Channel, string> = { call: "Call", sms: "SMS", email: "Email", mail: "Mail" };
const CHANNEL_TONE: Record<Channel, "blue" | "teal" | "violet" | "grey"> = { call: "blue", sms: "teal", email: "violet", mail: "grey" };
const ChannelPill = ({ channel }: { channel: string }) => (
  <Pill tone={CHANNEL_TONE[(channel as Channel)] ?? "grey"}>{CHANNEL_LABEL[(channel as Channel)] ?? channel}</Pill>
);

// ---- list --------------------------------------------------------------------
const LIST_STATUSES = ["draft", "active", "paused", "completed", "archived"];
const CampaignsList = ({ onOpen, onNew, onWizard }: { onOpen: (id: string) => void; onNew: (channel: Channel) => void; onWizard: () => void }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [channelTab, setChannelTab] = useState<Channel>("call");
  const [statusFilter, setStatusFilter] = useState<string>("__all__");
  const [search, setSearch] = useState("");
  useEffect(() => { api.loadCampaigns().then(setRows); }, []);
  const chanRows = rows.filter((r) => (r.channel ?? "call") === channelTab);
  const counts = LIST_STATUSES.reduce((a, s) => ({ ...a, [s]: chanRows.filter((r) => r.status === s).length }), {} as Record<string, number>);
  const chanCounts = CHANNELS.reduce((a, ch) => ({ ...a, [ch]: rows.filter((r) => (r.channel ?? "call") === ch).length }), {} as Record<string, number>);
  const shown = chanRows
    .filter((r) => statusFilter === "__all__" || r.status === statusFilter)
    .filter((r) => !search.trim() || r.name.toLowerCase().includes(search.trim().toLowerCase()));
  const isCall = channelTab === "call";
  return (
    <div className="space-y-4">
      {/* channel tabs */}
      <div className="flex items-center gap-1 border-b">
        {CHANNELS.map((ch) => {
          const disabled = ch === "mail";
          return (
            <button key={ch} disabled={disabled}
              onClick={() => !disabled && setChannelTab(ch)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${channelTab === ch ? "border-blue-600 text-blue-700 dark:text-blue-300" : disabled ? "border-transparent text-muted-foreground/40 cursor-not-allowed" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {CHANNEL_LABEL[ch]} <span className="tabular-nums text-xs opacity-70">{chanCounts[ch] ?? 0}</span>{disabled && <span className="ml-1 text-[10px]">(soon)</span>}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {LIST_STATUSES.map((s) => (
          <StatTile key={s} label={s} value={counts[s] ?? 0} active={statusFilter === s}
            onClick={() => setStatusFilter(statusFilter === s ? "__all__" : s)} />
        ))}
      </div>

      <MeToolbar>
        <Input placeholder="Search campaigns…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-64" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All statuses</SelectItem>{LIST_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex-1" />
        {isCall && <Button variant="outline" onClick={onWizard} className="border-orange-400 text-orange-600 hover:text-orange-700"><Sparkles className="mr-1 h-4 w-4" />Wizard</Button>}
        <Button onClick={() => onNew(channelTab)}><Plus className="mr-1 h-4 w-4" />New {CHANNEL_LABEL[channelTab]} Campaign</Button>
      </MeToolbar>

      <div className="rounded-lg border overflow-x-auto bg-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Channel</TableHead><TableHead>{isCall ? "Mode" : "Sequence"}</TableHead><TableHead>Status</TableHead>
            <TableHead>{isCall ? "Total Leads" : "Cohort"}</TableHead><TableHead>{isCall ? "Agents" : ""}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No {CHANNEL_LABEL[channelTab]} campaigns{search ? " match" : " yet"}.</TableCell></TableRow>
            ) : shown.map((c) => (
              <TableRow key={c.id} className="cursor-pointer" onClick={() => onOpen(c.id)}>
                <TableCell className="font-semibold text-blue-700 dark:text-blue-400">{c.name}</TableCell>
                <TableCell><ChannelPill channel={c.channel ?? "call"} /></TableCell>
                <TableCell>{isCall ? <Pill tone="grey">{c.dialing_mode}</Pill> : <span className="text-xs text-muted-foreground">drip</span>}</TableCell>
                <TableCell><CampaignStatusPill status={c.status} /></TableCell>
                <TableCell className="tabular-nums">{isCall ? c.total_leads : "—"}</TableCell>
                <TableCell className="tabular-nums">{isCall ? c.agent_count : ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ---- editor defaults ---------------------------------------------------------
const emptyCampaign: Partial<Campaign> = {
  name: "", status: "draft", channel: "call", max_sends_per_day: null, dialing_mode: "preview", call_order: "adaptive",
  max_attempts_per_record: 3, retry_value: 3, retry_unit: "hour", call_recording: true,
  suppress_federal_dnc: false, suppress_system_dnc: false, suppress_wireless: false, suppress_litigator: false,
  calling_hours_start: "08:00", calling_hours_end: "21:00", timezone: "America/New_York",
  call_result_set: ["Answering Machine", "Do Not Call", "No Answer", "Successful Sale", "Voicemail"],
  agent_script_id: null, auto_assign_criteria: null, auto_assign_enabled: true, smart_local_presence: false,
};

// editable chip list (Call Results)
const ChipsEditor = ({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) => {
  const [text, setText] = useState("");
  const add = () => { const t = text.trim(); if (t && !value.includes(t)) onChange([...value, t]); setText(""); };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold">
            {v}<button onClick={() => onChange(value.filter((x) => x !== v))} className="text-muted-foreground hover:text-red-600"><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Add a call result…" className="h-8 w-56" />
        <Button size="sm" variant="outline" onClick={add} disabled={!text.trim()}>Add</Button>
      </div>
    </div>
  );
};

// ---- Buy-new-numbers dialog (reuses twilio-numbers; PhonePage parity) ---------
const BuyNumberDialog = ({ open, onClose, onBought }: { open: boolean; onClose: () => void; onBought: (id: string, e164: string) => void }) => {
  const notify = useNotify();
  const [areaCode, setAreaCode] = useState("");
  const [contains, setContains] = useState("");
  const [results, setResults] = useState<{ phoneNumber: string; friendlyName?: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const doSearch = async () => {
    setBusy(true);
    try { setResults(await api.searchNumbers(areaCode.trim(), contains.trim())); }
    catch (e: any) { notify(e.message || "Search failed", { type: "error" }); }
    setBusy(false);
  };
  const doBuy = async (phoneNumber: string) => {
    setBusy(true);
    try {
      const id = await api.buyNumber(phoneNumber);
      if (id) { onBought(id, phoneNumber); notify(`Purchased ${formatPhone(phoneNumber)}`, { type: "success" }); onClose(); }
      else notify("Bought, but could not resolve the new number row — refresh Phone Numbers.", { type: "warning" });
    } catch (e: any) { notify(e.message || "Buy failed", { type: "error" }); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Buy a new Twilio number</DialogTitle></DialogHeader>
        <div className="flex items-end gap-2">
          <div className="space-y-1"><Label className="text-xs">Area code</Label><Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="754" className="h-9 w-24" /></div>
          <div className="space-y-1 flex-1"><Label className="text-xs">Contains (optional)</Label><Input value={contains} onChange={(e) => setContains(e.target.value)} placeholder="digits/letters" className="h-9" /></div>
          <Button onClick={doSearch} disabled={busy}>Search</Button>
        </div>
        <div className="max-h-64 overflow-y-auto rounded-md border divide-y">
          {results.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No results yet — search by area code.</p> :
            results.map((r) => (
              <div key={r.phoneNumber} className="flex items-center justify-between px-3 py-2">
                <div className="text-sm"><span className="font-semibold">{formatPhone(r.phoneNumber)}</span>{r.friendlyName && <span className="text-muted-foreground ml-2 text-xs">{r.friendlyName}</span>}</div>
                <Button size="sm" variant="secondary" onClick={() => doBuy(r.phoneNumber)} disabled={busy}><ShoppingCart className="mr-1 h-3.5 w-3.5" />Buy</Button>
              </div>
            ))}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- stepper meta ------------------------------------------------------------
const STEP_GROUPS = [
  { group: "Campaign Settings", items: [
    { id: "s-mode", label: "Dialing Mode" },
    { id: "s-agents", label: "Agents" },
    { id: "s-numbers", label: "Phone Numbers" },
    { id: "s-contacts", label: "Contact List" },
    { id: "s-config", label: "Campaign Config" },
  ]},
  { group: "Advanced Configuration", items: [
    { id: "s-order", label: "Calls Order" },
    { id: "s-behavior", label: "Dialing Behavior" },
    { id: "s-leadsheet", label: "Lead Sheet" },
    { id: "s-compliance", label: "Compliance & Hours" },
  ]},
];

// a labelled key/value row in the live summary
const SItem = ({ k, v, tone }: { k: string; v: ReactNode; tone?: "on" | "off" }) => (
  <div className="flex justify-between gap-3 py-0.5 text-xs">
    <span className="font-semibold text-muted-foreground">{k}</span>
    <span className={`font-bold text-right ${tone === "on" ? "text-green-600 dark:text-green-500" : tone === "off" ? "text-muted-foreground/60" : ""}`}>{v}</span>
  </div>
);

// small labelled toggle row (Compliance)
const ToggleRow = ({ label, sub, checked, onChange, badge }: { label: string; sub?: string; checked: boolean; onChange: (v: boolean) => void; badge?: string }) => (
  <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
    <div><div className="font-medium text-sm flex items-center gap-2">{label}{badge && <span className="rounded bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-400 text-[10px] font-bold px-1.5 py-0.5 tracking-wide">{badge}</span>}</div>{sub && <div className="text-xs text-muted-foreground">{sub}</div>}</div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

const CampaignEditor = ({ id, newChannel = "call", onBack, onWizard }: { id: string | null; newChannel?: Channel; onBack: () => void; onWizard: () => void }) => {
  const notify = useNotify();
  const [activeId, setActiveId] = useState<string | null>(id);
  const [configType, setConfigType] = useState<"new" | "clone" | "recycle">("new");
  const [sourceId, setSourceId] = useState("");        // source campaign for clone/recycle
  const [recycleName, setRecycleName] = useState("");
  const [preview, setPreview] = useState<{ total: number; connected: number; recyclable: number } | null>(null);
  const [launching, setLaunching] = useState(false);

  const [c, setC] = useState<Partial<Campaign>>({ ...emptyCampaign, channel: newChannel });
  const [templates, setTemplates] = useState<{ id: string; name: string; subject: string | null; body: string }[]>([]);
  const [masters, setMasters] = useState<{ id: string; code: string | null; name: string; kind: string; activated: boolean; cohort: number }[]>([]);
  const [enrolling, setEnrolling] = useState<string | null>(null);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [numberIds, setNumberIds] = useState<string[]>([]);
  const [steps, setSteps] = useState<DripStep[]>([]);
  const [opts, setOpts] = useState<any>({ users: [], phones: [], scripts: [], lists: [] });
  const [crit, setCrit] = useState<Record<string, string>>({});
  const [assignList, setAssignList] = useState("");
  const [saving, setSaving] = useState(false);
  const [canManage, setCanManage] = useState(false);
  const [canBuy, setCanBuy] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [activeStep, setActiveStep] = useState("s-mode");
  const [sourceCampaigns, setSourceCampaigns] = useState<any[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const started = activeId !== null;   // an actual campaign row exists (edit / after clone-recycle)

  const load = useCallback(async () => {
    setOpts(await api.loadOptions());
    if (activeId) {
      const d = await api.loadCampaign(activeId);
      setC(d.campaign); setAgentIds(d.agentIds); setNumberIds(d.numberIds); setSteps(d.steps);
      setCrit((d.campaign?.auto_assign_criteria as any) ?? {});
    }
  }, [activeId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const sb = getSupabaseClient();
    sb.rpc("capability_level", { p_cap: "manage_campaigns_automations" }).then(({ data }) => setCanManage(data === "full"));
    sb.rpc("capability_level", { p_cap: "manage_phone" }).then(({ data }) => setCanBuy(data === "full"));
    api.loadCampaigns().then(setSourceCampaigns);
  }, []);
  // recycle preview as the source changes
  useEffect(() => {
    if (configType === "recycle" && sourceId) api.recyclePreview(sourceId).then(setPreview).catch(() => setPreview(null));
    else setPreview(null);
  }, [configType, sourceId]);

  const channel = (c.channel ?? "call") as Channel;
  const isCall = channel === "call";
  // sms/email: load the channel's templates + the masters this base is composed into
  useEffect(() => {
    if (!isCall) api.loadTemplates(channel).then(setTemplates); else setTemplates([]);
  }, [isCall, channel]);
  useEffect(() => {
    if (!isCall && activeId) api.loadBaseMasters(activeId).then(setMasters); else setMasters([]);
  }, [isCall, activeId]);
  const doEnroll = async (masterId: string) => {
    if (!activeId) return notify("Save the campaign first", { type: "error" });
    setEnrolling(masterId);
    try {
      const n = await api.enrollCohort(masterId, activeId);
      notify(`Enrolled ${n} lead${n === 1 ? "" : "s"} into this sequence (drafts only — nothing sent)`, { type: "success" });
    } catch (e: any) { notify(e.message || "Enroll failed", { type: "error" }); }
    setEnrolling(null);
  };

  const set = (k: keyof Campaign, v: any) => setC((p) => ({ ...p, [k]: v }));
  const toggle = (arr: string[], setArr: (v: string[]) => void, val: string) =>
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  const goto = (secId: string) => {
    setActiveStep(secId);
    scrollRef.current?.querySelector(`#${secId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // config-type launchers (clone / recycle produce a draft, then drop into edit)
  const doClone = async () => {
    if (!sourceId) return notify("Pick a campaign to copy from", { type: "error" });
    setLaunching(true);
    try {
      const newId = await api.cloneCampaign(sourceId);
      notify("Cloned config, agents, numbers & drip into a new draft (no lead assignments)", { type: "success" });
      setConfigType("new"); setActiveId(newId);
    } catch (e: any) { notify(e.message || "Clone failed", { type: "error" }); }
    setLaunching(false);
  };
  const doRecycle = async () => {
    if (!sourceId) return notify("Pick a campaign to recycle", { type: "error" });
    setLaunching(true);
    try {
      const newId = await api.recycleCampaign(sourceId, recycleName.trim() || "");
      const n = preview?.recyclable ?? 0;
      notify(`Recycled into a new draft — ${n} non-connected lead${n === 1 ? "" : "s"} carried over (connected leads left behind)`, { type: "success" });
      setConfigType("new"); setActiveId(newId);
    } catch (e: any) { notify(e.message || "Recycle failed", { type: "error" }); }
    setLaunching(false);
  };

  const persist = async (statusOverride?: string) => {
    if (!c.name?.trim()) { notify("Name is required", { type: "error" }); return; }
    setSaving(true);
    const criteria = Object.fromEntries(Object.entries(crit).filter(([, v]) => v && String(v).trim()));
    const payload: any = { ...c, auto_assign_criteria: Object.keys(criteria).length ? criteria : null };
    if (statusOverride) payload.status = statusOverride;
    if (activeId) payload.id = activeId;
    const { data, error } = await api.saveCampaign(payload);
    if (error) { setSaving(false); return notify(`Save failed: ${error.message}`, { type: "error" }); }
    const cid = (data as any)?.id ?? activeId;
    await api.saveAgents(cid, agentIds);
    await api.saveNumbers(cid, numberIds);
    if (steps.length) await api.saveDrip(cid, c.name!, steps);
    setSaving(false);
    notify(statusOverride === "draft" ? "Saved as draft (won't dial until activated)" : "Campaign saved", { type: "success" });
    onBack();
  };
  // "Save Campaign" publishes a draft to active; otherwise keeps the chosen status.
  const saveCampaign = () => persist(c.status === "draft" ? "active" : undefined);
  const saveDraft = () => persist("draft");

  const doAssignList = async () => {
    if (!activeId || !assignList) return;
    const { data, error } = await api.assignByList(activeId, assignList);
    if (error) return notify(error.message, { type: "error" });
    notify(`Assigned ${data ?? 0} raw leads`, { type: "success" });
  };
  const doAutoAssign = async () => {
    if (!activeId) return notify("Save the campaign first", { type: "error" });
    const { data, error } = await api.applyAutoAssign(activeId);
    if (error) return notify(error.message, { type: "error" });
    notify(`Auto-assigned ${data ?? 0} matching raw leads`, { type: "success" });
  };

  const addStep = () => setSteps((s) => [...s, { step_order: s.length + 1, delay_value: 0, delay_unit: "day", action_type: "sms", body: "" }]);
  const setStep = (i: number, k: keyof DripStep, v: any) => setSteps((s) => s.map((st, j) => (j === i ? { ...st, [k]: v } : st)));

  // ---- derived labels for the summary panel ----------------------------------
  const nameOf = (list: any[], idv: string | null, key = "id", label = "name") => list.find((x) => x[key] === idv)?.[label];
  const agentNames = agentIds.map((aid) => nameOf(opts.users, aid, "id", "full_name")).filter(Boolean);
  const numberLabels = numberIds.map((nid) => { const p = opts.phones.find((x: any) => x.id === nid); return p ? formatPhone(p.e164) : null; }).filter(Boolean);
  const scriptName = c.agent_script_id ? nameOf(opts.scripts, c.agent_script_id) : "Default";
  const critEntries = Object.entries(crit).filter(([, v]) => v && String(v).trim());
  const contactSource = assignList ? (nameOf(opts.lists, assignList, "id", "title") ?? "List") : critEntries.length ? critEntries.map(([k, v]) => `${k}=${v}`).join(", ") : "—";

  // step "done" heuristics
  const done: Record<string, boolean> = {
    "s-mode": !!c.dialing_mode, "s-agents": agentIds.length > 0, "s-numbers": numberIds.length > 0,
    "s-contacts": !!assignList || critEntries.length > 0, "s-config": !!c.name?.trim(),
    "s-order": !!c.call_order, "s-behavior": true, "s-leadsheet": false, "s-compliance": true,
  };

  // ---- config-type launcher (only before a row exists) -----------------------
  const RadioPill = ({ v, label, badge }: { v: typeof configType; label: string; badge?: boolean }) => (
    <button onClick={() => setConfigType(v)} className={`flex items-center gap-2 font-semibold ${configType === v ? "text-foreground" : "text-muted-foreground"}`}>
      <span className={`h-4 w-4 rounded-full border-2 ${configType === v ? "border-blue-600 ring-2 ring-inset ring-blue-600" : "border-muted-foreground/40"}`} />
      {label}{badge && <span className="rounded bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-400 text-[10px] font-bold px-1.5 py-0.5">NEW</span>}
    </button>
  );

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)] -mx-2">
      {/* config-type bar (call only — clone/recycle are dialer-scope ops) */}
      {isCall ? (
        <div className="flex items-center gap-6 border-b bg-card px-4 py-3 flex-none">
          <RadioPill v="new" label="New Campaign" />
          <RadioPill v="clone" label="Create from Existing" badge />
          <RadioPill v="recycle" label="Recycle Existing" badge />
          <button className="ml-auto flex items-center gap-2 rounded-lg border border-orange-400 text-orange-600 px-3 py-1.5 font-semibold text-sm"
            onClick={onWizard}>
            <Sparkles className="h-4 w-4" /> Campaign Wizard <span className="rounded bg-teal-50 dark:bg-teal-950 text-teal-700 dark:text-teal-400 text-[10px] font-bold px-1.5 py-0.5">NEW</span>
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3 border-b bg-card px-4 py-3 flex-none">
          <ChannelPill channel={channel} />
          <span className="font-semibold text-sm">{CHANNEL_LABEL[channel]} base campaign</span>
          <span className="text-xs text-muted-foreground">— a drip sequence worked against the master cohort (draft-for-approval, D17)</span>
        </div>
      )}

      {/* clone / recycle launcher (shown until a draft row is created) */}
      {isCall && !started && configType !== "new" && (
        <div className="border-b bg-muted/30 px-4 py-3 flex-none">
          {!canManage && <p className="text-xs text-red-600 mb-2">Requires the Campaigns &amp; Automations capability (Admin).</p>}
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">Source campaign</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger className="h-9 w-72"><SelectValue placeholder="Pick a campaign…" /></SelectTrigger>
                <SelectContent>{sourceCampaigns.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.status}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {configType === "recycle" && (
              <div className="space-y-1"><Label className="text-xs">New campaign name</Label>
                <Input value={recycleName} onChange={(e) => setRecycleName(e.target.value)} placeholder="Recycled — …" className="h-9 w-72" />
              </div>
            )}
            {configType === "clone"
              ? <Button onClick={doClone} disabled={!canManage || !sourceId || launching}>{launching ? "Cloning…" : "Clone & edit"}</Button>
              : <Button onClick={doRecycle} disabled={!canManage || !sourceId || launching}>{launching ? "Recycling…" : "Recycle & edit"}</Button>}
          </div>
          {configType === "recycle" && preview && (
            <p className="text-xs text-muted-foreground mt-2">
              Source has <b>{preview.total}</b> leads · <b>{preview.connected}</b> already connected (left behind) ·{" "}
              <b className="text-foreground">{preview.recyclable}</b> non-connected will carry into the new draft.
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {configType === "clone"
              ? "Copies dial config, agents, numbers & drip into a new draft — no lead assignments."
              : "Clones the source config, then moves its non-connected leads (never a live pickup) into the new draft."}
          </p>
        </div>
      )}

      {/* the 3-col CALL config surface (once we're on a New flow or an existing/created row) */}
      {isCall && (configType === "new" || started) && (
        <div ref={scrollRef} className="grid grid-cols-[200px_1fr_280px] flex-1 overflow-y-auto">
          {/* STEPPER */}
          <div className="sticky top-0 self-start p-4 border-r">
            {STEP_GROUPS.map((g) => (
              <div key={g.group} className="mb-4">
                <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground/70 mb-2">{g.group}</h4>
                {g.items.map((it) => (
                  <button key={it.id} onClick={() => goto(it.id)}
                    className={`flex items-center gap-2.5 w-full text-left px-2.5 py-2 rounded-md text-sm font-medium mb-0.5 ${activeStep === it.id ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300" : "text-muted-foreground hover:bg-muted"}`}>
                    <span className={`h-[18px] w-[18px] rounded-full border-2 grid place-items-center flex-none ${done[it.id] ? "bg-green-600 border-green-600 text-white" : activeStep === it.id ? "border-blue-600" : "border-muted-foreground/40"}`}>
                      {done[it.id] && <Check className="h-2.5 w-2.5" />}
                    </span>
                    {it.label}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* CENTER FORM */}
          <div className="p-5 pb-24 space-y-4 min-w-0">
            <div id="s-mode" className="scroll-mt-4">
              <SectionCard title="Dialing Mode" sub="How agents place calls in this campaign.">
                <RadioCards cols={2} value={c.dialing_mode ?? "preview"} onChange={(v) => set("dialing_mode", v)}
                  options={[
                    { value: "preview", label: "Preview Dialer", desc: "Agent reviews each contact, then clicks to dial. Manual (non-ATDS)." },
                    { value: "predictive", label: "Predictive Dialer", desc: "Auto-dials multiple lines; connects on a live answer.", disabled: true, note: "Gated off (D35) until multi-line triggers are met." },
                  ]} />
              </SectionCard>
            </div>

            <div id="s-agents" className="scroll-mt-4">
              <SectionCard title="Agents" sub={`${agentIds.length} selected · who will dial this campaign`}>
                <ChipMultiSelect
                  options={opts.users.map((u: any) => ({ value: u.id, label: u.full_name, sub: u.email }))}
                  selected={agentIds} onToggle={(v) => toggle(agentIds, setAgentIds, v)}
                  empty="No active users." />
              </SectionCard>
            </div>

            <div id="s-numbers" className="scroll-mt-4">
              <SectionCard title="Phone Numbers" sub="Caller-ID numbers this campaign dials from · ≈ 100 dials / number / day"
                right={canBuy ? <Button size="sm" variant="outline" onClick={() => setBuyOpen(true)}><ShoppingCart className="mr-1 h-3.5 w-3.5" />Buy new</Button> : undefined}>
                <ChipMultiSelect
                  options={opts.phones.map((p: any) => ({ value: p.id, label: formatPhone(p.e164), sub: p.purpose }))}
                  selected={numberIds} onToggle={(v) => toggle(numberIds, setNumberIds, v)}
                  empty="No numbers configured." />
              </SectionCard>
            </div>

            <div id="s-contacts" className="scroll-mt-4">
              <SectionCard title="Contact Source" sub="Which raw leads this campaign works — a list, auto-criteria, or manual pick.">
                <div className="flex items-end gap-2">
                  <div className="space-y-1 flex-1"><Label className="text-xs">Assign a whole list</Label>
                    <Select value={assignList} onValueChange={setAssignList}>
                      <SelectTrigger><SelectValue placeholder="Pick a list…" /></SelectTrigger>
                      <SelectContent>{opts.lists.map((l: any) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button variant="secondary" onClick={doAssignList} disabled={!started || !assignList}>Assign list</Button>
                </div>
                <div className="rounded-md border bg-muted/30 p-3 space-y-2 mt-3">
                  <div className="flex items-center justify-between">
                    <div><div className="text-sm font-medium">Auto-assign new matching leads</div><div className="text-xs text-muted-foreground">New arrivals matching the criteria join automatically (on intake + on Apply).</div></div>
                    <Switch checked={c.auto_assign_enabled ?? true} onCheckedChange={(v) => set("auto_assign_enabled", v)} />
                  </div>
                  <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">Auto-assign criteria (all set fields must match)</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {(["case_type", "county", "source", "list"] as const).map((f) => (
                      <div key={f} className="space-y-1"><Label className="text-[10px] uppercase text-muted-foreground">{f}</Label>
                        <Input value={crit[f] ?? ""} onChange={(e) => setCrit((p) => ({ ...p, [f]: e.target.value }))} className="h-8" placeholder={f === "list" ? "list id" : ""} />
                      </div>
                    ))}
                  </div>
                  <Button size="sm" variant="secondary" onClick={doAutoAssign} disabled={!started}>Apply auto-assign criteria now</Button>
                  <p className="text-xs text-muted-foreground">Save the campaign to persist criteria before applying.</p>
                </div>
              </SectionCard>
            </div>

            <div id="s-config" className="scroll-mt-4">
              <SectionCard title="Campaign Configuration">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Campaign name</Label><Input value={c.name ?? ""} onChange={(e) => set("name", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Status</Label>
                    <Select value={c.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 col-span-2"><Label className="text-xs">Agent script</Label>
                    <Select value={c.agent_script_id ?? "__none__"} onValueChange={(v) => set("agent_script_id", v === "__none__" ? null : v)}>
                      <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
                      <SelectContent><SelectItem value="__none__">Default</SelectItem>{opts.scripts.map((s: any) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1 mt-3"><Label className="text-xs">Call Results (one-click disposition chips)</Label>
                  <ChipsEditor value={c.call_result_set ?? []} onChange={(v) => set("call_result_set", v)} />
                </div>
              </SectionCard>
            </div>

            <div id="s-order" className="scroll-mt-4">
              <SectionCard title="Calls Order" sub="The order the dialer works numbers, using the phone activity score.">
                <RadioCards cols={1} value={c.call_order ?? "adaptive"} onChange={(v) => set("call_order", v)}
                  options={[
                    { value: "adaptive", label: "Adaptive", desc: "Best-number-first by activity score, then oldest due." },
                    { value: "highest_score_first", label: "Highest score first", desc: "Work the most-likely-to-answer numbers first." },
                    { value: "lowest_score_first", label: "Lowest score first", desc: "Work the weakest scores first." },
                  ]} />
              </SectionCard>
            </div>

            <div id="s-behavior" className="scroll-mt-4">
              <SectionCard title="Dialing Behavior">
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Max calls/day</Label><Input type="number" value={c.max_calls_per_day ?? ""} onChange={(e) => set("max_calls_per_day", e.target.value ? Number(e.target.value) : null)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Max attempts/record</Label><Input type="number" value={c.max_attempts_per_record ?? 3} onChange={(e) => set("max_attempts_per_record", Number(e.target.value))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Retry every</Label><Input type="number" value={c.retry_value ?? 3} onChange={(e) => set("retry_value", Number(e.target.value))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Retry unit</Label>
                    <Select value={c.retry_unit} onValueChange={(v) => set("retry_unit", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["minute", "hour", "day"].map((u) => <SelectItem key={u} value={u} className="capitalize">{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">Predictive-only controls (Rapid Fire, Simultaneous Dials, Abandonment Timeout, AMD) appear when Predictive is enabled — deferred (D35).</p>
              </SectionCard>
            </div>

            <div id="s-leadsheet" className="scroll-mt-4">
              <SectionCard title="Lead Sheet" sub="A structured form the agent fills during the call (Forms/custom-fields engine).">
                <div className="rounded-md border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
                  Lead-sheet builder is deferred to CC-S2 (Forms engine). Calls use the agent script above for now.
                </div>
              </SectionCard>
            </div>

            <div id="s-compliance" className="scroll-mt-4">
              <SectionCard title="Compliance & Calling Hours">
                <ToggleRow label="Call Recording" checked={!!c.call_recording} onChange={(v) => set("call_recording", v)} />
                <ToggleRow label="Suppress Federal DNC" checked={!!c.suppress_federal_dnc} onChange={(v) => set("suppress_federal_dnc", v)} />
                <ToggleRow label="Suppress System DNC" checked={!!c.suppress_system_dnc} onChange={(v) => set("suppress_system_dnc", v)} />
                <ToggleRow label="Suppress Wireless Contacts" sub="Call landlines only" checked={!!c.suppress_wireless} onChange={(v) => set("suppress_wireless", v)} />
                <ToggleRow label="Suppress Litigator Contacts" checked={!!c.suppress_litigator} onChange={(v) => set("suppress_litigator", v)} />
                <ToggleRow label="Smart Local Presence" badge="NEW" sub="Match caller-ID area code to the lead's location (dialer wiring lands in CC-S2)" checked={!!c.smart_local_presence} onChange={(v) => set("smart_local_presence", v)} />
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <div className="space-y-1"><Label className="text-xs">Calling hours start</Label><Input type="time" value={c.calling_hours_start ?? "08:00"} onChange={(e) => set("calling_hours_start", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Calling hours end</Label><Input type="time" value={c.calling_hours_end ?? "21:00"} onChange={(e) => set("calling_hours_end", e.target.value)} /></div>
                  <div className="space-y-1"><Label className="text-xs">Timezone</Label><Input value={c.timezone ?? "America/New_York"} onChange={(e) => set("timezone", e.target.value)} /></div>
                </div>
              </SectionCard>
            </div>

            {/* drips kept from S3 — same live WS-2 runner, draft-for-approval (D17) */}
            <SectionCard title="Drips" sub="Draft-for-approval — never auto-sends to sellers (D17)"
              right={<Button variant="outline" size="sm" onClick={addStep}><Plus className="mr-1 h-4 w-4" />Add step</Button>}>
              {steps.length === 0 ? <p className="text-sm text-muted-foreground">No drip steps. Add one to enroll assigned raw leads.</p> :
                <div className="space-y-2">{steps.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-md border p-2 bg-background">
                    <span className="text-xs text-muted-foreground w-6">#{i + 1}</span>
                    <Input type="number" value={s.delay_value} onChange={(e) => setStep(i, "delay_value", Number(e.target.value))} className="h-8 w-16" />
                    <Select value={s.delay_unit} onValueChange={(v) => setStep(i, "delay_unit", v)}>
                      <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>{["minute", "hour", "day", "week"].map((u) => <SelectItem key={u} value={u} className="capitalize">{u}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={s.action_type} onValueChange={(v) => setStep(i, "action_type", v)}>
                      <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>{["sms", "email", "task", "direct_mail"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input value={s.action_type === "task" ? (s.task_name ?? "") : (s.body ?? "")}
                      onChange={(e) => setStep(i, s.action_type === "task" ? "task_name" : "body", e.target.value)}
                      placeholder={s.action_type === "task" ? "task name" : "message body"} className="h-8 flex-1" />
                    <Button size="icon" variant="ghost" onClick={() => setSteps((st) => st.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                ))}</div>}
              <p className="text-xs text-muted-foreground">Steps run on the live WS-2 runner against the raw lead's shadow record; sellers are never auto-contacted (D17).</p>
            </SectionCard>
          </div>

          {/* LIVE SUMMARY */}
          <div className="sticky top-0 self-start h-full overflow-y-auto border-l bg-blue-50/40 dark:bg-blue-950/20 p-4">
            <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 truncate">{c.name || "Untitled campaign"}</h4>
            <p className="text-xs text-muted-foreground mb-3">{configType === "new" && !started ? "New campaign" : "Editing"} · {c.status}</p>
            <div className="mb-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-blue-700 dark:text-blue-400 mb-1">Core</div>
              <SItem k="Dialing Mode" v={cap(c.dialing_mode ?? "preview")} />
              <SItem k="Agents" v={agentNames.length ? (agentNames.length <= 2 ? agentNames.join(", ") : `${agentNames.length} agents`) : "—"} />
              <SItem k="Phone Numbers" v={numberLabels.length ? (numberLabels.length === 1 ? numberLabels[0] : `${numberLabels.length} numbers`) : "—"} />
              <SItem k="Contact Source" v={contactSource} />
              <SItem k="Call Results" v={`${(c.call_result_set ?? []).length} set`} />
              <SItem k="Agent Script" v={scriptName ?? "Default"} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-blue-700 dark:text-blue-400 mb-1">Advanced</div>
              <SItem k="Calls Order" v={cap((c.call_order ?? "adaptive").replace(/_/g, " "))} />
              <SItem k="Call Recording" v={c.call_recording ? "Enabled" : "Off"} tone={c.call_recording ? "on" : "off"} />
              <SItem k="Suppress Fed DNC" v={c.suppress_federal_dnc ? "On" : "Off"} tone={c.suppress_federal_dnc ? "on" : "off"} />
              <SItem k="Suppress Sys DNC" v={c.suppress_system_dnc ? "On" : "Off"} tone={c.suppress_system_dnc ? "on" : "off"} />
              <SItem k="Suppress Wireless" v={c.suppress_wireless ? "On" : "Off"} tone={c.suppress_wireless ? "on" : "off"} />
              <SItem k="Suppress Litigator" v={c.suppress_litigator ? "On" : "Off"} tone={c.suppress_litigator ? "on" : "off"} />
              <SItem k="Smart Local Presence" v={c.smart_local_presence ? "On" : "Off"} tone={c.smart_local_presence ? "on" : "off"} />
              <SItem k="Max Attempts" v={c.max_attempts_per_record ?? 3} />
              <SItem k="Retry Time" v={`${c.retry_value ?? 3} ${c.retry_unit ?? "hour"}${(c.retry_value ?? 3) === 1 ? "" : "s"}`} />
              <SItem k="Calling Hours" v={`${c.calling_hours_start ?? "08:00"}–${c.calling_hours_end ?? "21:00"}`} />
              <SItem k="Time Zone" v={(c.timezone ?? "America/New_York").replace("America/", "")} />
            </div>
          </div>
        </div>
      )}

      {/* SMS / EMAIL base surface (cohort-driven; draft-only) */}
      {!isCall && (
        <div className="flex-1 overflow-y-auto p-5 pb-24 space-y-4 max-w-3xl">
          <SectionCard title="Campaign Configuration">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Campaign name</Label><Input value={c.name ?? ""} onChange={(e) => set("name", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Status</Label>
                <Select value={c.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Message Sequence" sub="Draft-for-approval — the live WS-2 runner drafts each step; a human approves & sends in Work → Drafts (D17)."
            right={<Button variant="outline" size="sm" onClick={() => setSteps((s) => [...s, { step_order: s.length + 1, delay_value: 0, delay_unit: "day", action_type: channel, body: "" }])}><Plus className="mr-1 h-4 w-4" />Add step</Button>}>
            <div className="flex items-center gap-2 mb-3">
              <Label className="text-xs whitespace-nowrap">Insert from template</Label>
              <Select value="" onValueChange={(tid) => { const t = templates.find((x) => x.id === tid); if (t) setSteps((s) => [...s, { step_order: s.length + 1, delay_value: 0, delay_unit: "day", action_type: channel, subject: t.subject ?? null, body: t.body }]); }}>
                <SelectTrigger className="h-8 w-72"><SelectValue placeholder={templates.length ? "Pick a template…" : "No " + channel + " templates yet (Settings → Templates)"} /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-[11px] text-muted-foreground">merge fields: <code>{"{{first_name}}"}</code> <code>{"{{property_address}}"}</code> <code>{"{{city}}"}</code></span>
            </div>
            {steps.length === 0 ? <p className="text-sm text-muted-foreground">No steps yet. Add one (or insert a template) to build the sequence.</p> :
              <div className="space-y-2">{steps.map((s, i) => (
                <div key={i} className="rounded-md border p-2 bg-background space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-6">#{i + 1}</span>
                    <span className="text-xs text-muted-foreground">wait</span>
                    <Input type="number" value={s.delay_value} onChange={(e) => setStep(i, "delay_value", Number(e.target.value))} className="h-8 w-16" />
                    <Select value={s.delay_unit} onValueChange={(v) => setStep(i, "delay_unit", v)}>
                      <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
                      <SelectContent>{["minute", "hour", "day", "week"].map((u) => <SelectItem key={u} value={u} className="capitalize">{u}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground ml-auto">{CHANNEL_LABEL[channel]}</span>
                    <Button size="icon" variant="ghost" onClick={() => setSteps((st) => st.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                  {channel === "email" && <Input value={s.subject ?? ""} onChange={(e) => setStep(i, "subject", e.target.value)} placeholder="Subject" className="h-8" />}
                  <textarea value={s.body ?? ""} onChange={(e) => setStep(i, "body", e.target.value)} placeholder="Message body — merge fields allowed" className="w-full min-h-16 rounded-md border bg-background p-2 text-sm" />
                </div>
              ))}</div>}
          </SectionCard>

          <SectionCard title="Audience" sub="This base works the cohort of every master it is composed into. Enrollment is manual — nothing auto-sends.">
            {!started ? <p className="text-sm text-muted-foreground">Save the campaign, then compose it into a master (via the reconcile) to work a cohort.</p> :
              masters.length === 0 ? <p className="text-sm text-muted-foreground">Not composed into any master yet. Attach this base to the Default Master and run the reconcile (an admin op), then reload.</p> :
              <div className="space-y-2">{masters.map((m) => (
                <div key={m.id} className="flex items-center gap-2 rounded-md border p-2">
                  <span className="font-mono text-sm font-semibold">{m.code ?? m.name}</span>
                  <Pill tone={m.kind === "weekly" ? "blue" : "grey"}>{m.kind}</Pill>
                  {m.activated ? <Pill tone="green" dot>Launched</Pill> : <Pill tone="amber" dot>Not launched</Pill>}
                  <span className="text-xs text-muted-foreground">{m.cohort} lead{m.cohort === 1 ? "" : "s"}</span>
                  <div className="flex-1" />
                  <Button size="sm" variant="secondary" disabled={!m.activated || enrolling === m.id} onClick={() => doEnroll(m.id)}>
                    {enrolling === m.id ? "Enrolling…" : "Enroll cohort"}
                  </Button>
                </div>
              ))}</div>}
            <p className="text-xs text-muted-foreground mt-2">Enroll drafts one message per lead into Work → Drafts — a human approves & sends. A master must be launched first.</p>
          </SectionCard>

          <SectionCard title="Send Window & Throttle" sub="Advisory — enforcement lands when Phase 3 wires channel consumption. SMS also respects the hard compliance floor in config.sms_send_window.">
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1"><Label className="text-xs">Window start</Label><Input type="time" value={c.calling_hours_start ?? "08:00"} onChange={(e) => set("calling_hours_start", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Window end</Label><Input type="time" value={c.calling_hours_end ?? "21:00"} onChange={(e) => set("calling_hours_end", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Timezone</Label><Input value={c.timezone ?? "America/New_York"} onChange={(e) => set("timezone", e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Max sends/day <span className="text-muted-foreground">(advisory)</span></Label><Input type="number" value={c.max_sends_per_day ?? ""} onChange={(e) => set("max_sends_per_day", e.target.value ? Number(e.target.value) : null)} /></div>
            </div>
          </SectionCard>

          {channel === "email" && (
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-300">
              Email sends need <code>RESEND_API_KEY</code> set (Supabase secrets). Until then, steps still draft into Work → Drafts, but <code>send-draft</code> returns <code>501 email not configured</code>.
            </div>
          )}
        </div>
      )}

      {/* sticky action bar */}
      <div className="flex items-center justify-center gap-3 border-t bg-card px-4 py-3 flex-none">
        <Button variant="ghost" onClick={onBack}><X className="mr-1 h-4 w-4" />Cancel</Button>
        {(configType === "new" || started) && <>
          <Button variant="outline" onClick={saveDraft} disabled={saving}>{saving ? "Saving…" : "Save as Draft"}</Button>
          <Button onClick={saveCampaign} disabled={saving}><Check className="mr-1 h-4 w-4" />{saving ? "Saving…" : "Save Campaign"}</Button>
        </>}
      </div>

      <BuyNumberDialog open={buyOpen} onClose={() => setBuyOpen(false)}
        onBought={(nid) => { api.loadOptions().then(setOpts); setNumberIds((n) => n.includes(nid) ? n : [...n, nid]); }} />
    </div>
  );
};

// ---- shell -------------------------------------------------------------------
const CampaignsPage = () => {
  const [view, setView] = useState<{ mode: "list" | "edit" | "wizard"; id: string | null; channel?: Channel }>({ mode: "list", id: null });
  const toList = () => setView({ mode: "list", id: null });
  return (
    <div className="p-2 max-w-[1200px]">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Megaphone className="h-5 w-5" /> Campaigns</h1>
      <p className="text-sm text-muted-foreground mb-4">
        Multi-channel campaigns — Call (dialer) · SMS · Email base sequences over the master cohort.
      </p>
      {view.mode === "list" ? (
        <CampaignsList onOpen={(id) => setView({ mode: "edit", id })} onNew={(channel) => setView({ mode: "edit", id: null, channel })} onWizard={() => setView({ mode: "wizard", id: null })} />
      ) : view.mode === "wizard" ? (
        <CampaignWizard onDone={toList} onCancel={toList} />
      ) : (
        <CampaignEditor id={view.id} newChannel={view.channel ?? "call"} onBack={toList} onWizard={() => setView({ mode: "wizard", id: null })} />
      )}
    </div>
  );
};

CampaignsPage.path = "/campaigns";
export default CampaignsPage;
